import React, { useState, useMemo } from "react";
import {
    UserAddOutlined,
    EditOutlined,
    DeleteOutlined,
    LogoutOutlined,
    GiftOutlined,
    UserSwitchOutlined,
} from "@ant-design/icons";
import { Button, Input, Tooltip, message } from "antd";
import { History, LayoutGrid, UserPlus, Pencil, Trash2, ArrowRightLeft, LogOut, Gift } from "lucide-react";

function cn(...c) {
    return c.filter(Boolean).join(" ");
}

const shellClass =
    "relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-[0_18px_60px_-30px_rgba(0,0,0,0.9)]";

const TYPE_ICONS = {
    create: <UserAddOutlined />,
    edit: <EditOutlined />,
    delete: <DeleteOutlined />,
    transfer: <UserSwitchOutlined />,
    logout: <LogoutOutlined />,
    promo_create: <GiftOutlined />,
};

const TYPE_STYLES = {
    create: {
        icon: "border-emerald-400/35 bg-emerald-500/12 text-emerald-300",
        badge: "border-emerald-400/30 bg-emerald-500/10 text-emerald-100 hover:bg-emerald-500/15",
    },
    edit: {
        icon: "border-sky-400/35 bg-sky-500/12 text-sky-300",
        badge: "border-sky-400/30 bg-sky-500/10 text-sky-100 hover:bg-sky-500/15",
    },
    delete: {
        icon: "border-rose-400/35 bg-rose-500/12 text-rose-300",
        badge: "border-rose-400/30 bg-rose-500/10 text-rose-100 hover:bg-rose-500/15",
    },
    transfer: {
        icon: "border-violet-400/35 bg-violet-500/12 text-violet-300",
        badge: "border-violet-400/30 bg-violet-500/10 text-violet-100 hover:bg-violet-500/15",
    },
    logout: {
        icon: "border-white/20 bg-white/[0.08] text-white/55",
        badge: "border-white/15 bg-white/[0.06] text-white/65 hover:bg-white/10",
    },
    promo_create: {
        icon: "border-amber-400/35 bg-amber-500/12 text-amber-300",
        badge: "border-amber-400/30 bg-amber-500/10 text-amber-100 hover:bg-amber-500/15",
    },
};

/** Демо-данные (позже можно заменить API) */
const actionsData = [
    {
        id: "1",
        name: "John Doe",
        action: "Создал пользователя",
        time: "2024-12-06 14:10",
        type: "create",
        relatedName: "Jane Smith",
        objectId: "12345",
    },
    {
        id: "2",
        name: "Anna Smith",
        action: "Редактировал промо акцию",
        time: "2024-12-06 13:45",
        type: "edit",
        relatedName: "Holiday Promo",
        objectId: "PROMO-6789",
    },
    {
        id: "3",
        name: "Michael Brown",
        action: "Удалил пользователя",
        time: "2024-12-06 13:30",
        type: "delete",
        relatedName: "Mark Green",
        objectId: "54321",
    },
    {
        id: "4",
        name: "Sarah Connor",
        action: "Создал промо акцию",
        time: "2024-12-06 13:15",
        type: "promo_create",
        relatedName: "Winter Sale",
        objectId: "PROMO-4567",
    },
    {
        id: "5",
        name: "Tom Wilson",
        action: "Передал админу",
        time: "2024-12-06 12:50",
        type: "transfer",
    },
    {
        id: "6",
        name: "Alice Cooper",
        action: "Вышел из системы",
        time: "2024-12-06 12:30",
        type: "logout",
    },
];

const FILTERS = [
    { id: "all", label: "Все", Icon: LayoutGrid },
    { id: "create", label: "Создание", Icon: UserPlus },
    { id: "edit", label: "Редактирование", Icon: Pencil },
    { id: "delete", label: "Удаление", Icon: Trash2 },
    { id: "transfer", label: "Передача", Icon: ArrowRightLeft },
    { id: "logout", label: "Выход", Icon: LogOut },
    { id: "promo_create", label: "Промо", Icon: Gift },
];

const RecentActionsFull = () => {
    const [filter, setFilter] = useState("all");
    const [searchQuery, setSearchQuery] = useState("");

    const filteredActions = useMemo(
        () =>
            actionsData.filter(
                (a) =>
                    (filter === "all" || a.type === filter) &&
                    a.name.toLowerCase().includes(searchQuery.toLowerCase())
            ),
        [filter, searchQuery]
    );

    const handleBadgeClick = (objectId) => {
        message.info(`Объект: ${objectId} (заглушка перехода)`);
    };

    const filterBtnClass = (active) =>
        cn(
            "!flex !h-9 !w-9 !items-center !justify-center !rounded-xl !border !p-0 !transition-colors",
            active
                ? "!border-emerald-400/45 !bg-emerald-500/20 !text-emerald-100"
                : "!border-white/10 !bg-white/[0.06] !text-white/50 hover:!border-white/20 hover:!bg-white/10 hover:!text-white/85"
        );

    return (
        <div className="flex min-h-[calc(100dvh-1.5rem)] flex-1 flex-col gap-4 p-4 text-white sm:p-5">
            <section className={cn(shellClass, "shrink-0 p-4 sm:p-5")}>
                <div className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-indigo-500/10 blur-3xl" />
                <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3">
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06]">
                            <History className="h-5 w-5 text-indigo-300/90" strokeWidth={2} />
                        </span>
                        <div>
                            <h1 className="text-lg font-semibold tracking-tight text-white/95 sm:text-xl">
                                Последние действия
                            </h1>
                            <p className="text-[12px] text-white/45">Лента событий админки (демо-данные)</p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {FILTERS.map((f) => {
                            const { Icon: FilterIcon } = f;
                            const active = filter === f.id;
                            return (
                                <Tooltip key={f.id} title={f.label}>
                                    <Button
                                        type="text"
                                        onClick={() => setFilter(f.id)}
                                        className={filterBtnClass(active)}
                                        icon={<FilterIcon size={16} strokeWidth={2} />}
                                    />
                                </Tooltip>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className={cn(shellClass, "shrink-0 p-4 sm:p-5")}>
                <Input
                    placeholder="Поиск по имени пользователя…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    allowClear
                    className="!rounded-xl !border-white/10 !bg-white/[0.06] py-2.5 text-white/90 placeholder:!text-white/35 hover:!border-white/[0.18] focus:!border-emerald-400/50 [&_.ant-input]:!text-white/90"
                />
            </section>

            <section className={cn(shellClass, "flex min-h-0 flex-1 flex-col overflow-hidden")}>
                <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
                    <div className="relative">
                        {/* Линия за иконками: z-0, иначе визуально «режет» круги с полупрозрачным фоном */}
                        <div
                            className="pointer-events-none absolute bottom-0 left-[17px] top-0 z-0 w-px bg-gradient-to-b from-white/20 via-white/10 to-transparent sm:left-[18px]"
                            aria-hidden
                        />

                        <div className="relative z-[1] space-y-4">
                            {filteredActions.length > 0 ? (
                                filteredActions.map((action, index) => {
                                    const styles = TYPE_STYLES[action.type] || TYPE_STYLES.logout;
                                    const icon = TYPE_ICONS[action.type] || <EditOutlined />;
                                    return (
                                        <div
                                            key={`${action.id}-${index}`}
                                            className="relative flex gap-4 pl-1 sm:gap-5"
                                        >
                                            <div className="relative z-10 h-9 w-9 shrink-0">
                                                <div
                                                    className="absolute inset-0 rounded-full bg-[#070A12]"
                                                    aria-hidden
                                                />
                                                <div
                                                    className={cn(
                                                        "relative flex h-9 w-9 items-center justify-center rounded-full border",
                                                        styles.icon
                                                    )}
                                                >
                                                    <span className="text-[14px]">{icon}</span>
                                                </div>
                                            </div>
                                            <div
                                                className={cn(
                                                    "min-w-0 flex-1 rounded-xl border border-white/[0.08] bg-white/[0.03] px-4 py-3 backdrop-blur-sm",
                                                    "sm:flex sm:items-center sm:justify-between sm:gap-4"
                                                )}
                                            >
                                                <div className="min-w-0">
                                                    <p className="text-[13px] text-white/90">
                                                        <span className="font-semibold text-white">{action.name}</span>{" "}
                                                        <span className="text-white/70">{action.action}</span>
                                                    </p>
                                                    <p className="mt-1 font-mono text-[11px] text-white/40">
                                                        {action.time}
                                                    </p>
                                                </div>
                                                {action.relatedName && action.objectId && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleBadgeClick(action.objectId)}
                                                        className={cn(
                                                            "mt-3 shrink-0 rounded-full border px-3 py-1 text-[12px] font-medium transition-colors sm:mt-0",
                                                            styles.badge
                                                        )}
                                                    >
                                                        {action.relatedName}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="flex flex-col items-center py-16 text-white/45">
                                    <History className="mb-2 h-12 w-12 opacity-35" strokeWidth={1.25} />
                                    <p className="text-[13px]">Действия не найдены</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default RecentActionsFull;
